import { FC } from 'react';
import { useLocation } from 'react-router-dom';

import { useLingui } from '@lingui/react';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import FeedIcon from '@mui/icons-material/Feed';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PollIcon from '@mui/icons-material/Poll';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Box } from '@mui/material';

import NavSection from 'src/components/NavSection';
import { StepperTitle } from 'src/components/StepperTitle';
import { useQuoteQuery } from 'src/components/rfp/QuoteProvider';
import { appendAccessToken } from 'src/utils/appendAccessToken';

export const QuoteEvaluationSidebar: FC = () => {
    const { i18n } = useLingui();
    const location = useLocation();

    const { quoteQuery } = useQuoteQuery();

    const quoteId = quoteQuery?.sourceQuote?.id;
    const quoteIntegerId = quoteQuery?.sourceQuote?.integerId;
    const projectName = quoteQuery?.sourceQuote?.projectName;

    const sections = [
        {
            title: '',
            items: [
                {
                    title: i18n._('Project briefing'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/summary`,
                    icon: <FeedIcon fontSize="small" />,
                },
                {
                    title: i18n._('Questions & answers'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/questions`,
                    icon: <ContactSupportIcon fontSize="small" />,
                },
                {
                    title: i18n._('Proposals'),
                    path: appendAccessToken(
                        `${window.APADUA_SOURCE}app/project/${quoteIntegerId}/client/proposals`,
                    ),
                    external: true,
                    icon: <TableChartIcon fontSize="small" />,
                },
                {
                    title: i18n._('Vendors'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/vendors`,
                    icon: <BusinessIcon fontSize="small" />,
                },
                {
                    title: i18n._('Evaluations'),
                    path: appendAccessToken(
                        `${window.APADUA_SOURCE}app/project/${quoteIntegerId}/client/technical_evaluation`,
                    ),
                    external: true,
                    icon: <PollIcon fontSize="small" />,
                },
                {
                    title: i18n._('Project configuration'),
                    path: appendAccessToken(
                        `${window.APADUA_SOURCE}app/project/${quoteIntegerId}/client/configuration`,
                    ),
                    external: true,
                    icon: <BuildIcon fontSize="small" />,
                },
                {
                    title: i18n._('User management'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/users`,
                    icon: <ManageAccountsIcon fontSize="small" />,
                },
                {
                    title: i18n._('Announcements'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/announcements`,
                    icon: <ChatIcon fontSize="small" />,
                },
                {
                    title: i18n._('Internal Documents'),
                    path: `/dashboard/rfp/${quoteId}/evaluation/documents`,
                    icon: <InsertDriveFileIcon fontSize="small" />,
                },
            ],
        },
    ];

    return (
        <Box sx={{ my: 2, px: 2, pb: 2 }}>
            {projectName ? (
                <StepperTitle
                    title={projectName}
                    status={
                        quoteQuery?.sourceQuote?.status === 'on hold'
                            ? 'onHold'
                            : quoteQuery?.sourceQuote?.status
                    }
                />
            ) : null}
            <Box>
                {sections.map((section) => (
                    <NavSection
                        key={section.title}
                        pathname={location.pathname}
                        sx={{
                            '& + &': {
                                mt: 3,
                            },
                        }}
                        {...section}
                    />
                ))}
            </Box>
        </Box>
    );
};
